/**
 * Analytics identity.
 *
 * Three ids are emitted with every event so that "is this the same session?"
 * stays a reporting decision rather than something baked into the client:
 * - `tabInstanceId` — unique per document, never persisted.
 * - `browserSessionId` — shared across tabs, rolls over after an idle gap.
 * - `userId` — supplied by the caller from auth.
 */

const BROWSER_SESSION_KEY = 'analytics.browserSession';

/** Matches the InactivityProvider timeout so a session ends when the app considers the user gone. */
export const SESSION_IDLE_MS = 15 * 60 * 1000;

interface StoredBrowserSession {
  id: string;
  lastSeenAt: number;
}

export const createId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// sessionStorage is deliberately avoided: it is copied into duplicated tabs, so it is
// neither reliably per-tab nor reliably shared.
const TAB_INSTANCE_ID = createId();

export const getTabInstanceId = (): string => TAB_INSTANCE_ID;

const readStoredSession = (): StoredBrowserSession | null => {
  try {
    const raw = window.localStorage.getItem(BROWSER_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredBrowserSession>;
    if (typeof parsed?.id !== 'string' || typeof parsed?.lastSeenAt !== 'number') return null;
    return { id: parsed.id, lastSeenAt: parsed.lastSeenAt };
  } catch {
    return null;
  }
};

const writeStoredSession = (session: StoredBrowserSession): void => {
  try {
    window.localStorage.setItem(BROWSER_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage disabled or full — fall back to the in-memory id for this tab.
  }
};

let fallbackSessionId = TAB_INSTANCE_ID;
let fallbackLastSeenAt = Date.now();

/**
 * Returns the shared browser session id, minting a new one when the previous
 * session has been idle for longer than {@link SESSION_IDLE_MS}. Calling this
 * also refreshes the session's last-seen timestamp.
 */
export const getBrowserSessionId = (now: number = Date.now()): string => {
  const stored = readStoredSession();

  if (stored) {
    const id = now - stored.lastSeenAt < SESSION_IDLE_MS ? stored.id : createId();
    writeStoredSession({ id, lastSeenAt: now });
    fallbackSessionId = id;
    fallbackLastSeenAt = now;
    return id;
  }

  const id = now - fallbackLastSeenAt < SESSION_IDLE_MS ? fallbackSessionId : createId();
  writeStoredSession({ id, lastSeenAt: now });
  fallbackSessionId = id;
  fallbackLastSeenAt = now;
  return id;
};

/** Test seam — resets the module-level fallbacks. */
export const resetIdentityForTests = (): void => {
  fallbackSessionId = TAB_INSTANCE_ID;
  fallbackLastSeenAt = Date.now();
  try {
    window.localStorage.removeItem(BROWSER_SESSION_KEY);
  } catch {
    // ignore
  }
};
